import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Video,
  ExternalLink,
  Users,
  CheckCircle,
  Clock,
} from 'lucide-react';
import './LessonViewer.css';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import {
  DocumentViewer,
  VideoViewer,
  ScormViewer,
  UrlViewer,
  ILTSViewer,
} from './Components';
import { useInstructorLearningsStore } from '@/stores/InstructorStore/InstructorLearningsStore';
import { getLessonContentBaseUrl } from '@/utils/assetUrl';
import { useScormUrl } from '@/hooks/useScormUrl';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5111';

export default function LessonViewer() {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Use store instead of local state
  const {
    lessonDetails: lesson,
    loadingLessonDetails: loading,
    errorLessonDetails: error,
    fetchLessonDetails,
  } = useInstructorLearningsStore();

  const courseTitle = location.state?.courseTitle || 'Course';

  // Fetch dynamic SCORM URL if lesson is SCORM type
  const { scormUrl } = useScormUrl(
    lesson?.contentType === 'scorm' ? lesson.lesson_content_scorm : undefined
  );

  // Fetch lesson data from store
  useEffect(() => {
    if (lessonId && courseId) {
      console.log('LessonViewer - Fetching lesson data for lessonId:', lessonId, 'courseId:', courseId);
      fetchLessonDetails(Number(courseId), Number(lessonId));
    }
  }, [lessonId, courseId, fetchLessonDetails]);

  const handleBack = () => {
    navigate(`/trainer/course/${courseId}`, {
      state: { sourceLabel: 'My Courses' },
    });
  };

  const getContentUrl = () => {
    if (!lesson) return '';

    console.log('LessonViewer - Full lesson data:', lesson);
    console.log('LessonViewer - Lesson type:', lesson.lessonType);
    console.log('LessonViewer - Content type:', lesson.contentType);

    const baseUrl = getLessonContentBaseUrl();  // Use relative URLs to leverage proxy

    switch (lesson.contentType) {
      case 'document':
        console.log('LessonViewer - Document file:', lesson.lessonContentDocument);
        return lesson.lessonContentDocument
          ? `${baseUrl}/documents/${lesson.lessonContentDocument}`
          : '';
      case 'mp4':
        console.log('LessonViewer - MP4 file:', lesson.lesson_content_mp4);
        return lesson.lesson_content_mp4
          ? `${baseUrl}/mp4/${lesson.lesson_content_mp4}`
          : '';
      case 'scorm':
        console.log('LessonViewer - SCORM file:', lesson.lesson_content_scorm);
        console.log('LessonViewer - SCORM URL (dynamic):', scormUrl);
        return scormUrl || '';
      case 'url':
        console.log('LessonViewer - URL:', lesson.lesson_content_url);
        return lesson.lesson_content_url || '';
      default:
        console.log('LessonViewer - Unknown content type');
        return '';
    }
  };

  const renderContent = () => {
    if (!lesson) return null;

    // ILTS Lesson
    if (lesson.lessonType === 'ILTS') {
      return (
        <ILTSViewer
          iltsType={lesson.iltsType || 'Online'}
          startDate={lesson.startDate}
          startTime={lesson.startTime}
          endDate={lesson.endDate}
          endTime={lesson.endTime}
          eventVenue={lesson.eventVenue}
          meetUrl={lesson.meetUrl}
          description={lesson.description}
        />
      );
    }

    // Content-Based Lesson
    const contentUrl = getContentUrl();

    if (!contentUrl) {
      return (
        <div className="lesson-viewer__no-content">
          <FileText size={48} color="#9ca3af" />
          <p>No content available for this lesson</p>
        </div>
      );
    }

    // Render based on content type
    switch (lesson.contentType) {
      case 'document':
        return <DocumentViewer contentUrl={contentUrl} title={lesson.title} />;

      case 'mp4':
        return <VideoViewer contentUrl={contentUrl} title={lesson.title} />;

      case 'scorm':
        return <ScormViewer contentUrl={contentUrl} title={lesson.title} />;

      case 'url':
        return <UrlViewer contentUrl={contentUrl} title={lesson.title} />;

      default:
        return (
          <div className="lesson-viewer__no-content">
            <FileText size={48} color="#9ca3af" />
            <p>Unsupported content type</p>
          </div>
        );
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen size="lg" />;
  }

  if (error) {
    return (
      <ErrorDisplay
        fullScreen
        title="Failed to Load Lesson"
        message={error}
        type="error"
        showRetry
        onRetry={() => fetchLessonDetails(Number(courseId), Number(lessonId))}
      />
    );
  }

  if (!lesson) {
    return (
      <ErrorDisplay
        fullScreen
        title="Lesson Not Found"
        message="The lesson you're looking for doesn't exist."
        type="error"
      />
    );
  }

  return (
    <div className="lesson-viewer">
      {/* Header */}
      <div className="lesson-viewer__header">
        <button onClick={handleBack} className="lesson-viewer__back-btn">
          <ArrowLeft size={20} />
          Back to Course
        </button>
        <div className="lesson-viewer__breadcrumb">
          <span
            className="lesson-viewer__breadcrumb-clickable"
            onClick={() => navigate(-1)}
            style={{ cursor: 'pointer' }}
          >
            {courseTitle}
          </span>
          <span className="lesson-viewer__breadcrumb-separator">›</span>
          <span className="lesson-viewer__breadcrumb-current">{lesson.title}</span>
        </div>
      </div>

      {/* Lesson Info */}
      <div className="lesson-viewer__info">
        <div className="lesson-viewer__info-left">
          <h1 className="lesson-viewer__title">{lesson.title}</h1>
          <div className="lesson-viewer__meta">
            {lesson.lessonType === 'Content-Based' && lesson.contentType && (
              <span className="lesson-viewer__badge">
                {lesson.contentType === 'mp4' && <Video size={14} />}
                {lesson.contentType === 'document' && <FileText size={14} />}
                {lesson.contentType === 'scorm' && <FileText size={14} />}
                {lesson.contentType === 'url' && <ExternalLink size={14} />}
                {lesson.contentType.toUpperCase()}
              </span>
            )}
            {lesson.lessonType === 'ILTS' && (
              <span className="lesson-viewer__badge lesson-viewer__badge--ilts">
                <Users size={14} />
                {lesson.iltsType} Session
              </span>
            )}
            {lesson.duration && (
              <span className="lesson-viewer__duration">
                <Clock size={14} />
                {lesson.duration}
              </span>
            )}
          </div>
        </div>
        <button className="lesson-viewer__complete-btn">
          <CheckCircle size={18} />
          Mark as Complete
        </button>
      </div>

      {/* Content Area */}
      <div className="lesson-viewer__content">{renderContent()}</div>

      {/* Description */}
      {lesson.description && lesson.lessonType === 'Content-Based' && (
        <div className="lesson-viewer__description">
          <h3>Description</h3>
          <p>{lesson.description}</p>
        </div>
      )}
    </div>
  );
}
